import {
  FetchHttpClient,
  HttpApiClient,
  HttpClient,
  HttpClientRequest,
} from "@effect/platform";
import { Api } from "@redotech/effect-api-shared/rpc/api";
import { Effect, Layer, ManagedRuntime } from "effect";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { REDO_EFFECT_API_URL } from "../core/config";
import { AuthContext } from "./auth";
import { TeamInfoContext } from "./team-info";

const _clientEffect = HttpApiClient.make(Api);
export type WidgetApiClient = Effect.Effect.Success<typeof _clientEffect>;

export interface EffectApiWidgetContext {
  runPromise: <A>(
    effect: Effect.Effect<A, never, HttpClient.HttpClient>,
  ) => Promise<A>;
  getClient: () => WidgetApiClient;
}

export const EffectApiWidgetClientContext = createContext<
  EffectApiWidgetContext | undefined
>(undefined);

EffectApiWidgetClientContext.displayName = "EffectApiWidgetClientContext";

function createWidgetHttpClientLayer(
  widgetSlug: string,
  authToken: string | undefined,
) {
  return Layer.effect(
    HttpClient.HttpClient,
    Effect.gen(function* () {
      const client = yield* HttpClient.HttpClient;
      return client.pipe(
        HttpClient.mapRequest((req) => {
          let r = HttpClientRequest.setHeader("x-widget-id", widgetSlug)(req);
          if (authToken) {
            r = HttpClientRequest.setHeader(
              "Authorization",
              `Bearer ${authToken}`,
            )(r);
          }
          return r;
        }),
      );
    }),
  ).pipe(Layer.provide(FetchHttpClient.layer));
}

export const EffectApiWidgetClientProvider = memo(
  function EffectApiWidgetClientProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const teamInfo = useContext(TeamInfoContext);
    const auth = useContext(AuthContext);
    const widgetSlug = teamInfo?.widgetSlug;
    const authToken = auth?.token;

    const context = useMemo(():
      | (EffectApiWidgetContext & { dispose: () => Promise<void> })
      | undefined => {
      if (!widgetSlug) {
        return undefined;
      }

      const layer = createWidgetHttpClientLayer(widgetSlug, authToken);
      const runtime = ManagedRuntime.make(layer);

      let client: WidgetApiClient | null = null;

      return {
        runPromise: <A,>(
          effect: Effect.Effect<A, never, HttpClient.HttpClient>,
        ) => runtime.runPromise(effect),

        getClient: () => {
          if (!client) {
            client = runtime.runSync(
              HttpApiClient.make(Api, { baseUrl: REDO_EFFECT_API_URL }),
            );
          }
          return client;
        },

        dispose: () => runtime.dispose(),
      };
    }, [widgetSlug, authToken]);

    useEffect(() => {
      return () => {
        void context?.dispose();
      };
    }, [context]);

    const value = useMemo(
      () =>
        context
          ? { runPromise: context.runPromise, getClient: context.getClient }
          : undefined,
      [context],
    );

    return (
      <EffectApiWidgetClientContext.Provider value={value}>
        {children}
      </EffectApiWidgetClientContext.Provider>
    );
  },
);
